import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';
import { config } from '../config/config';
import Navbar from './Navbar';
import Footer from './Footer';

const QueryForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    query: '',
    service: '',
    location: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Check reCAPTCHA if enabled
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (recaptchaSiteKey && !recaptchaValue) {
      setSubmitStatus('recaptcha');
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 3000);
      return;
    }

    // EmailJS configuration from environment variables
    const emailjsServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const emailjsTemplateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const emailjsPublicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    try {
      // Prepare form data for EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email || 'No email provided',
        phone: formData.phone,
        service: formData.service || 'Not specified',
        location: formData.location || 'Not specified',
        message: formData.query,
        to_email: config.business.email,
        business_name: config.business.name,
        date: new Date().toLocaleString('en-IN', { 
          timeZone: 'Asia/Kolkata',
          dateStyle: 'full',
          timeStyle: 'short'
        })
      };

      // Send email via EmailJS if configured
      if (emailjsServiceId && emailjsTemplateId && emailjsPublicKey) {
        await emailjs.send(
          emailjsServiceId,
          emailjsTemplateId,
          templateParams,
          emailjsPublicKey
        );
      } else {
        // Fallback: Use mailto if EmailJS not configured
        const emailBody = `
New Query from JS Kuber Website:

Name: ${formData.name}
Phone: ${formData.phone}
Email: ${formData.email || 'Not provided'}
Service: ${formData.service || 'Not specified'}
Location: ${formData.location || 'Not specified'}
Query: ${formData.query}

Date: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
        `;
        const mailtoLink = `mailto:${config.business.email}?subject=New Query from Website&body=${encodeURIComponent(emailBody)}`;
        window.location.href = mailtoLink;
      }

      // WhatsApp functionality can be enabled by uncommenting below:
      // const whatsappNumber = config.business.whatsapp || config.business.contact;
      // const whatsappMessage = `*New Query from J S Kuber Website*\n\n` +
      //   `Name: ${formData.name}\n` +
      //   `Phone: ${formData.phone}\n` +
      //   `Email: ${formData.email || 'Not provided'}\n` +
      //   `Service: ${formData.service || 'Not specified'}\n` +
      //   `Location: ${formData.location || 'Not specified'}\n` +
      //   `Query: ${formData.query}\n\n` +
      //   `_This message was sent from the website contact form_`;
      // window.open(`https://wa.me/91${whatsappNumber.replace(/\D/g, '')}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        query: '',
        service: '',
        location: ''
      });
      
      // Reset reCAPTCHA if enabled
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaValue(null);
      }

      // Reset status after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="query-page">
      <Navbar />
      <section className="process" style={{ minHeight: '80vh', paddingTop: '100px' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{ maxWidth: '700px', margin: '0 auto' }}
        >
          <p className="sec-label">Get In Touch</p>
          <h2 className="sec-title" style={{ color: 'var(--black)' }}>
            SUBMIT <span style={{ color: 'var(--gold)' }}>YOUR QUERY</span>
          </h2>
          <p style={{ textAlign: 'center', color: 'var(--gray)', marginBottom: '40px' }}>
            Fill out the form below and we'll get back to you as soon as possible.
          </p>

          <motion.form
            onSubmit={handleSubmit}
            style={{
              background: 'var(--white)',
              padding: '40px',
              border: '3px solid var(--black)',
              borderRadius: '8px'
            }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div style={{ display: 'grid', gap: '20px', marginBottom: '24px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '8px',
                  color: 'var(--black)'
                }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '14px',
                    fontSize: '15px',
                    border: '2px solid var(--black)',
                    background: 'var(--white)',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '8px',
                    color: 'var(--black)'
                  }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '14px',
                      fontSize: '15px',
                      border: '2px solid var(--black)',
                      background: 'var(--white)',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '8px',
                    color: 'var(--black)'
                  }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '14px',
                      fontSize: '15px',
                      border: '2px solid var(--black)',
                      background: 'var(--white)',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '8px',
                    color: 'var(--black)'
                  }}>
                    Service Interested
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '14px',
                      fontSize: '15px',
                      border: '2px solid var(--black)',
                      background: 'var(--white)',
                      fontFamily: 'inherit'
                    }}
                  >
                    <option value="">Select Service</option>
                    <option value="Sell Gold">Sell Gold</option>
                    <option value="Sell Silver">Sell Silver</option>
                    <option value="Buy Mortgaged Gold">Buy Mortgaged Gold</option>
                    <option value="Home Pickup">Home Pickup</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '8px',
                    color: 'var(--black)'
                  }}>
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Your location"
                    style={{
                      width: '100%',
                      padding: '14px',
                      fontSize: '15px',
                      border: '2px solid var(--black)',
                      background: 'var(--white)',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '8px',
                  color: 'var(--black)'
                }}>
                  Your Query *
                </label>
                <textarea
                  name="query"
                  value={formData.query}
                  onChange={handleChange}
                  required
                  rows="5"
                  style={{
                    width: '100%',
                    padding: '14px',
                    fontSize: '15px',
                    border: '2px solid var(--black)',
                    background: 'var(--white)',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>

            {submitStatus === 'success' && (
              <div style={{
                padding: '16px',
                background: '#4CAF50',
                color: 'white',
                marginBottom: '20px',
                textAlign: 'center',
                fontWeight: '700'
              }}>
                ✅ Query submitted successfully! We'll contact you soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div style={{
                padding: '16px',
                background: '#f44336',
                color: 'white',
                marginBottom: '20px',
                textAlign: 'center',
                fontWeight: '700'
              }}>
                ❌ Error submitting query. Please try again or call us directly.
              </div>
            )}

            {submitStatus === 'recaptcha' && (
              <div style={{
                padding: '16px',
                background: '#ff9800',
                color: 'white',
                marginBottom: '20px',
                textAlign: 'center',
                fontWeight: '700'
              }}>
                ⚠️ Please complete the reCAPTCHA verification.
              </div>
            )}

            {/* reCAPTCHA - Only show if site key is configured */}
            {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
              <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={(value) => setRecaptchaValue(value)}
                  theme="light"
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="submit"
                className="btn-big filled"
                disabled={isSubmitting}
                style={{ minWidth: '200px' }}
              >
                {isSubmitting ? 'Submitting...' : '📧 Submit Query'}
              </button>
              <Link to="/" className="btn-big empty">
                ← Back to Home
              </Link>
            </div>
          </motion.form>

          <div style={{
            marginTop: '40px',
            padding: '30px',
            background: 'var(--cream)',
            border: '3px solid var(--black)',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '24px',
              letterSpacing: '2px',
              marginBottom: '16px'
            }}>
              OR CONTACT US DIRECTLY
            </h3>
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href={`tel:${config.business.contact}`}
                className="btn-big filled"
                style={{ textDecoration: 'none' }}
              >
                📞 Call {config.business.contact}
              </a>
              <a
                href={`https://wa.me/91${(config.business.whatsapp || config.business.contact).replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-big empty"
                style={{ textDecoration: 'none' }}
              >
                💬 WhatsApp
              </a>
            </div>
          </div>
        </motion.div>
      </section>
      <Footer />
    </div>
  );
};

export default QueryForm;
